import { useState, useEffect } from 'react';
import { customersApi, productsApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { CustomerStep } from './create-contract/CustomerStep';
import { ProductStep } from './create-contract/ProductStep';

const EMPTY_NEW_CUSTOMER = {
    nome: '',
    cognome: '',
    ragione_sociale: '',
    tipo: 'privato',
    telefono: '',
    email: '',
    codice_fiscale: ''
};

export default function CreateContractDialog({ open, onOpenChange, onSuccess }) {
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerTab, setCustomerTab] = useState('existing');
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        customer_id: '',
        new_customer: { ...EMPTY_NEW_CUSTOMER },
        product_id: '',
        indirizzo_impianto: '',
        note: ''
    });

    useEffect(() => {
        if (open) {
            loadData();
        }
    }, [open]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [customersRes, productsRes] = await Promise.all([
                customersApi.getAll(),
                productsApi.getAll({ active_only: true })
            ]);
            setCustomers(customersRes.data || []);
            setProducts(productsRes.data || []);
        } catch (error) {
            console.error('Errore caricamento dati:', error);
            toast.error('Errore nel caricamento dei dati');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            customer_id: '',
            new_customer: { ...EMPTY_NEW_CUSTOMER },
            product_id: '',
            indirizzo_impianto: '',
            note: ''
        });
        setCustomerSearch('');
        setCustomerTab('existing');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (customerTab === 'existing' && !formData.customer_id) {
            toast.error('Seleziona un cliente');
            return;
        }
        if (customerTab === 'new' && !formData.new_customer.nome) {
            toast.error('Il nome del cliente è obbligatorio');
            return;
        }
        if (!formData.product_id) {
            toast.error('Seleziona un prodotto');
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                product_id: formData.product_id,
                indirizzo_impianto: formData.indirizzo_impianto,
                note: formData.note
            };

            if (customerTab === 'existing') {
                payload.customer_id = formData.customer_id;
            } else {
                payload.new_customer = formData.new_customer;
            }

            if (onSuccess) {
                await onSuccess(payload);
            }

            resetForm();
            onOpenChange(false);
        } catch (error) {
            console.error('Errore creazione contratto:', error);
            toast.error(error.response?.data?.detail || 'Errore nella creazione del contratto');
        } finally {
            setSubmitting(false);
        }
    };

    const filteredCustomers = customers.filter(c => {
        if (!customerSearch) return true;
        const search = customerSearch.toLowerCase();
        return (
            c.nome?.toLowerCase().includes(search) ||
            c.cognome?.toLowerCase().includes(search) ||
            c.ragione_sociale?.toLowerCase().includes(search) ||
            c.email?.toLowerCase().includes(search) ||
            c.codice_fiscale?.toLowerCase().includes(search)
        );
    });

    const selectedCustomer = customers.find(c => c.id === formData.customer_id);
    const selectedProduct = products.find(p => p.id === formData.product_id);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="create-contract-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl">Nuovo Contratto</DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                        <ScrollArea className="flex-1 pr-4" style={{maxHeight: 'calc(70vh - 120px)'}}>
                            <div className="space-y-6">
                                <CustomerStep
                                    customerTab={customerTab}
                                    setCustomerTab={setCustomerTab}
                                    customerSearch={customerSearch}
                                    setCustomerSearch={setCustomerSearch}
                                    filteredCustomers={filteredCustomers}
                                    selectedCustomer={selectedCustomer}
                                    formData={formData}
                                    setFormData={setFormData}
                                />

                                <ProductStep
                                    products={products}
                                    selectedProduct={selectedProduct}
                                    formData={formData}
                                    setFormData={setFormData}
                                />

                                {/* STEP 3: Info aggiuntive */}
                                <div className="space-y-3 pt-4 border-t">
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs font-medium">3</div>
                                        <h3 className="font-medium text-muted-foreground">Info Aggiuntive (opzionale)</h3>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label>Indirizzo Impianto</Label>
                                            <Input
                                                value={formData.indirizzo_impianto}
                                                onChange={(e) => setFormData({...formData, indirizzo_impianto: e.target.value})}
                                                placeholder="Via Roma 1, 20100 Milano (MI)"
                                                data-testid="contract-indirizzo"
                                            />
                                            <p className="text-xs text-muted-foreground">
                                                Può essere completato nello step "Inserimento Contratto"
                                            </p>
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Note</Label>
                                            <Textarea
                                                value={formData.note}
                                                onChange={(e) => setFormData({...formData, note: e.target.value})}
                                                placeholder="Note aggiuntive per la pratica..."
                                                rows={2}
                                                data-testid="contract-note"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ScrollArea>

                        <DialogFooter className="mt-4 pt-4 border-t">
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                                Annulla
                            </Button>
                            <Button 
                                type="submit" 
                                disabled={submitting}
                                data-testid="submit-contract-button"
                            >
                                {submitting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Creazione...
                                    </>
                                ) : (
                                    'Crea Contratto'
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
